import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useState } from 'react'

import useIsSuperAdmin from '../../../hooks/useIsSuperAdmin'
import { startImpersonation } from '../../../utils/impersonationService'

import { useUserData } from './hooks/useUserData'
import { useCurrentAdminProfile } from './hooks/useCurrentAdminProfile'

import { UserHeader } from './components/UserHeader'
import { UserInfo } from './components/UserInfo'
import { RolesSection } from './components/RolesSection'
import { DiagnosisSection } from './components/DiagnosisSection'
import { Studies } from './components/Studies'
import { UserHistorySection } from './components/UserHistorySection'
import { AccountStatus } from './components/AccountStatus'

export default function UserPage() {
  // 1. Custom hooks
  const { user, profile, ref, uid, triggerRefresh } = useUserData()
  const { adminName, adminInitials } = useCurrentAdminProfile()
  const isSuperAdmin = useIsSuperAdmin()
  const toast = useToast()

  // 2. Local state
  const [editStudyName, setEditStudyName] = useState<string | null>(null)
  const [isActingAsUser, setIsActingAsUser] = useState(false)
  const [impersonationReason, setImpersonationReason] = useState('')
  const {
    isOpen: isReasonOpen,
    onOpen: openReason,
    onClose: closeReason,
  } = useDisclosure()

  // 3. Early return if no user data
  if (!user) return null

  // 4. Event handlers
  const normalizedReason = impersonationReason.replace(/\s+/g, ' ').trim()
  const reasonIsInvalid =
    normalizedReason.length > 0 &&
    (normalizedReason.length < 8 || normalizedReason.length > 500)

  const handleActAsUser = async () => {
    if (!isSuperAdmin) return
    if (normalizedReason.length < 8 || normalizedReason.length > 500) return
    setIsActingAsUser(true)
    try {
      await startImpersonation(uid, normalizedReason)
      closeReason()
    } catch (error) {
      setIsActingAsUser(false)
      toast({
        title: 'Failed to start impersonation',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  // 5. Render
  return (
    <Stack spacing={5}>
      <UserHeader user={user} profile={profile} />

      <UserInfo
        user={user}
        profile={profile}
        uid={uid}
        isSuperAdmin={isSuperAdmin}
        isActingAsUser={isActingAsUser}
        onActAsUser={isSuperAdmin ? openReason : () => undefined}
      />

      <Modal
        isOpen={isReasonOpen}
        onClose={isActingAsUser ? () => undefined : closeReason}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Act as {profile?.firstName || 'this user'}</ModalHeader>
          <ModalCloseButton isDisabled={isActingAsUser} />
          <ModalBody>
            <FormControl isInvalid={reasonIsInvalid}>
              <FormLabel>Reason for impersonation</FormLabel>
              <Textarea
                value={impersonationReason}
                onChange={(event) => setImpersonationReason(event.target.value)}
                minLength={8}
                maxLength={500}
                autoFocus
                placeholder='For example: SUP-1234 — assisting with monthly check-in'
              />
              {reasonIsInvalid ? (
                <FormErrorMessage>
                  Enter between 8 and 500 characters.
                </FormErrorMessage>
              ) : (
                <FormHelperText>
                  Required for the impersonation audit trail.
                </FormHelperText>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              variant='ghost'
              onClick={closeReason}
              isDisabled={isActingAsUser}
            >
              Cancel
            </Button>
            <Button
              colorScheme='purple'
              onClick={handleActAsUser}
              isLoading={isActingAsUser}
              loadingText='Starting'
              isDisabled={
                normalizedReason.length < 8 || normalizedReason.length > 500
              }
            >
              Start session
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <RolesSection user={user} userRef={ref} adminName={adminName} />

      <DiagnosisSection
        profile={profile}
        user={user}
        userRef={ref}
        uid={uid}
        isSuperAdmin={isSuperAdmin}
        adminName={adminName}
        adminInitials={adminInitials}
      />

      <Stack spacing={3}>
        <Studies
          uid={uid}
          onUpdate={triggerRefresh}
          editStudyId={editStudyName}
          setEditStudyId={setEditStudyName}
        />
        <UserHistorySection user={user} />
      </Stack>

      <AccountStatus
        lockedDate={user.lockedDate}
        causeOfDeath={user.profile?.causeOfDeath}
        uid={uid}
        archived={user.archived}
      />
    </Stack>
  )
}
